import "./EditSpecialHoursPopup.scss";
import { useEffect, useState } from "react";
import Popup from "../../../hoc/Popup/Popup";
import { toast } from "react-toastify";
import HandleConfirmationPopup from "../../HandleConfirmation/HandleConfirmationPopup";
import { ISpecialHoursFacility } from "../../../types/facility";
import {
  createSpecialHours,
  deleteSpecialHours,
  updateSpecialHours,
} from "../../../api/facility/facilityOpeningTimes";
import WeekDaySelector from "../../../hoc/DatePicker/Datepicker";

import {
  COURT_LABELS,
  FacilityCourtType,
} from "../../../pages/AdminHandleFacility/mergeHours";
import { listCourts } from "../../../api/courts/courts";
import { ICourt } from "../../../types/api";
import { formatToIsoDate } from "../../../shared/dateUtils";
import {
  IOpeningHoursConflict,
  findOpeningHoursConflicts,
} from "../openingHoursConflicts";
import OpeningHoursConflictConfirmation from "../OpeningHoursConflictConfirmation";

interface IEditSpecialHoursPopupProps {
  showPopup: boolean;
  onClose: () => void;
  onConfirm: () => void;
  currentSpecialHours?: ISpecialHoursFacility;
  /** Every court type this row covers — the edit applies to all of them. */
  courtTypes: FacilityCourtType[];
}

function EditSpecialHoursPopup(props: IEditSpecialHoursPopupProps) {
  const { showPopup, onClose, onConfirm, courtTypes, currentSpecialHours } =
    props;
  const [showHandleConfirmationPopup, setShowHandleConfirmationPopup] =
    useState(false);
  const [
    showHandleDeleteConfirmationPopup,
    setShowHandleDeleteConfirmationPopup,
  ] = useState(false);
  const [showDoubleConfirmationPopup, setShowDoubleConfirmationPopup] =
    useState(false);

  const handleConfirm = () => {
    onConfirm();
    handleClose();
  };
  const handleClose = () => {
    setSpecialHoursData(initialSpecialData);
    setStartTime("07:00:00");
    setEndTime("23:00:00");
    onClose();
  };
  const [loading, setLoading] = useState<boolean>(false);
  // Bookings the change would make unbookable, listed in the second confirm.
  const [conflicts, setConflicts] = useState<IOpeningHoursConflict[]>([]);
  const [courts, setCourts] = useState<ICourt[]>([]);
  // An empty conflicts list means "none" only if the lookup actually ran.
  const [conflictCheckFailed, setConflictCheckFailed] = useState(false);

  /**
   * Between the two confirmations: look up what the change would hide, so the
   * second one can name the bookings instead of warning in the abstract.
   */
  const prepareDoubleConfirmation = async () => {
    setShowHandleConfirmationPopup(false);
    // Several queries plus a name lookup per rehab member, so this is long
    // enough to look like nothing happened when the first popup closes.
    const checkingToast = toast.loading("Kontrollerar bokningar...");
    try {
      const found = await findOpeningHoursConflicts({
        date: formatToIsoDate(date).split("T")[0],
        courtTypes: courtTypes,
        closed: specialHoursData.closed,
        openingHours: startTime,
        closingHours: endTime,
      });
      setConflicts(found);
      setConflictCheckFailed(false);
      toast.dismiss(checkingToast);
    } catch (error: any) {
      // A failed lookup must not read as "no bookings", and must not block
      // either — an outage in the query would otherwise lock the whole screen.
      setConflictCheckFailed(true);
      toast.update(checkingToast, {
        render: error?.message ?? "Kunde inte kontrollera bokningar",
        type: "error",
        isLoading: false,
        autoClose: 4000,
      });
      setConflicts([]);
    } finally {
      setShowDoubleConfirmationPopup(true);
    }
  };

  // Only for turning a court_id into "B4" in the conflict list.
  useEffect(() => {
    if (!showPopup || courts.length) return;
    listCourts()
      .then(setCourts)
      .catch(() => setCourts([]));
  }, [showPopup]);

  const [date, setDate] = useState(new Date());
  const [startTime, setStartTime] = useState("07:00:00");
  const [endTime, setEndTime] = useState("23:00:00");
  const initialSpecialData: ISpecialHoursFacility = {
    date: new Date().toISOString(),
    opening_hours: "07:00:00",
    closing_hours: "23:00:00",
    court_type: courtTypes[0] ?? "INDOOR",
    alias: "",
    closed: false,
  };

  const [specialHoursData, setSpecialHoursData] =
    useState<ISpecialHoursFacility>(initialSpecialData);

  // The date the record is stored under, captured when the popup opens. The
  // form's own date changes as the admin edits, so it cannot be used to tell
  // the backend where the record currently lives.
  const [originalDate, setOriginalDate] = useState<string>();

  const weekdays = {
    Måndag: "monday",
    Tisdag: "tuesday",
    Onsdag: "wednesday",
    Torsdag: "thursday",
    Fredag: "friday",
    Lördag: "saturday",
    Söndag: "sunday",
  };
  const handleTimeChange = (
    timeType: "start" | "end",
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const timeValue = e.target.value;
    if (timeType === "start") {
      setStartTime(timeValue);
    } else {
      setEndTime(timeValue);
    }
  };

  const handleTimeBlur = (
    timeType: "start" | "end",
    e: React.FocusEvent<HTMLInputElement>
  ) => {
    let time = e.target.value;
    let parts = time.split(":");

    if (parts.length === 2) {
      time += ":00";
    } else if (parts.length === 1 && time.includes(":")) {
      time += "00:00";
    } else if (parts.length === 1) {
      time = time.padStart(2, "0") + ":00:00";
    }

    if (timeType === "start") {
      setStartTime(time);
    } else {
      setEndTime(time);
    }
  };

  const handleInputChange = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
  ) => {
    setSpecialHoursData({
      ...specialHoursData,
      [e.target.name]: e.target.value,
    });
  };
  const handleClosedToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isClosed = e.target.checked;

    setSpecialHoursData((prev) => ({
      ...prev,
      closed: isClosed,
    }));
  };
  const handleUpdateSpecialHours = async () => {
    setLoading(true);
    specialHoursData.opening_hours = startTime;
    specialHoursData.closing_hours = endTime;
    specialHoursData.date = date.toISOString();
    // The date is part of the primary key, so the backend needs the date the
    // record is actually stored under to locate it. This has to be the
    // snapshot taken when the popup opened — the line above has already
    // overwritten the live value with the new date.
    specialHoursData.previous_date = originalDate;
    if (specialHoursData.closing_hours < specialHoursData.opening_hours) {
      toast.error("Stängningstiden kan inte vara tidigare än öppningstiden");
      setLoading(false);
      return;
    }
    if (specialHoursData.alias === "") {
      toast.error("Namn är obligatoriskt");
      setLoading(false);
      return;
    }
    // The row stands for the same day on several courts; each is its own
    // record, so this is one write per type and they can fail independently.
    const succeeded: string[] = [];
    const failed: string[] = [];

    for (const type of courtTypes) {
      try {
        const response = await updateSpecialHours({
          ...specialHoursData,
          court_type: type,
        });
        if (response && !("code" in response)) {
          succeeded.push(COURT_LABELS[type]);
        } else {
          failed.push(COURT_LABELS[type]);
        }
      } catch (error: any) {
        failed.push(COURT_LABELS[type]);
      }
    }

    setLoading(false);

    if (succeeded.length > 0) {
      toast.success(
        `Dagsaktuell inställning uppdaterad: ${succeeded.join(", ")}`,
      );
    }
    if (failed.length > 0) {
      toast.error(`Misslyckades för: ${failed.join(", ")}`);
    }
    if (succeeded.length > 0) {
      handleConfirm();
    }
  };
  const handleDeleteSpecialHours = async () => {
    const removed: string[] = [];
    const failed: string[] = [];

    for (const type of courtTypes) {
      try {
        const response = await deleteSpecialHours(
          specialHoursData.alias,
          type,
          (originalDate ?? specialHoursData.date)?.split("T")[0]
        );
        if (response && !("code" in response)) {
          removed.push(COURT_LABELS[type]);
        } else {
          failed.push(COURT_LABELS[type]);
        }
      } catch (error: any) {
        failed.push(COURT_LABELS[type]);
      }
    }

    if (removed.length > 0) {
      toast.success(`Dagsaktuell inställning raderad: ${removed.join(", ")}`);
    }
    if (failed.length > 0) {
      toast.error(`Misslyckades för: ${failed.join(", ")}`);
    }
    if (removed.length > 0) {
      handleConfirm();
    }
  };

  useEffect(() => {
    if (showPopup && currentSpecialHours) {
      // Copy, don't share: the submit handler assigns onto this object, and a
      // shared reference would mutate the prop too.
      setSpecialHoursData({ ...currentSpecialHours });
      setOriginalDate(currentSpecialHours.date);
      setStartTime(currentSpecialHours.opening_hours);
      setEndTime(currentSpecialHours.closing_hours);
      setDate(new Date(currentSpecialHours.date));
    }
  }, [showPopup]);
  return (
    <Popup
      showPopup={props.showPopup}
      onClose={handleClose}
      leftTopElement={
        <span className="text-m">
          Redigera dagsaktuell inställning {currentSpecialHours?.alias}
        </span>
      }
    >
      <div className="general-popup-wrapper">
        <form
          className="general-popup-form"
          onSubmit={(e) => {
            e.preventDefault();
            setShowHandleConfirmationPopup(true);
          }}
        >
          <div className="general-popup-form-group">
            <div className="general-popup-form-field">
              <label htmlFor="alias" className="text-s">
                Namn
              </label>
              <input
                type="text"
                name="alias"
                value={specialHoursData.alias}
                onChange={handleInputChange}
                className="text-m"
              />
              <label htmlFor="date" className="text-s">
                Datum
              </label>
              <WeekDaySelector
                textM
                includeWeekdays={false}
                value={date}
                onChange={(date) => setDate(date)}
              />
              <div className="general-popup-flex-between">
                <label className="text-s">Gäller banor</label>
                <div className="popup-court-badges text-s">
                  {courtTypes.map((type) => (
                    <span key={type} className={`court-badge ${type}`}>
                      {COURT_LABELS[type]}
                    </span>
                  ))}
                </div>
              </div>
              {!specialHoursData.closed && (
                <>
                  <div className="general-popup-flex-between">
                    <label htmlFor="memberType" className="text-s">
                      Start
                    </label>
                    <input
                      type="time"
                      id="startTime"
                      value={startTime}
                      className="text-m"
                      onBlur={(e) => handleTimeBlur("start", e)}
                      onChange={(e) => handleTimeChange("start", e)}
                    />
                  </div>

                  <div className="general-popup-flex-between">
                    <label htmlFor="memberType" className="text-s">
                      Slut
                    </label>
                    <input
                      type="time"
                      id="endTime"
                      value={endTime}
                      className="text-m"
                      onBlur={(e) => handleTimeBlur("end", e)}
                      onChange={(e) => handleTimeChange("end", e)}
                    />
                  </div>
                </>
              )}
              <label htmlFor="closed" className="text-m">
                Ska det vara stängt under detta interval ?
              </label>
              <div className="general-popup-flex-between">
                <span className="text-m secondary-text">Stängt</span>
                <label className="toggle text-m">
                  <input
                    type="checkbox"
                    name="closed"
                    checked={specialHoursData.closed}
                    onChange={(e) => handleClosedToggle(e)}
                  />
                  <span className="slider round"></span>
                </label>
              </div>
            </div>
          </div>

          <div className="general-popup-bottom-buttons-wrapper flex-between">
            <button
              type="button"
              className="deleteButton text-m"
              onClick={() => setShowHandleDeleteConfirmationPopup(true)}
            >
              Radera inställning
            </button>
            <button type="submit" className="text-m">
              Redigera dagsaktuella inställning
            </button>
          </div>
        </form>
      </div>
      <HandleConfirmationPopup
        showPopup={showHandleDeleteConfirmationPopup}
        onClose={() => setShowHandleDeleteConfirmationPopup(false)}
        onConfirm={() => {
          handleDeleteSpecialHours();
          setShowHandleDeleteConfirmationPopup(false);
        }}
        confirmText="Ja"
        questionText="Är du säker på att du vill radera denna dagsaktuella inställning?"
        denyText="Avbryt"
      />
      <HandleConfirmationPopup
        showPopup={showHandleConfirmationPopup}
        onClose={() => setShowHandleConfirmationPopup(false)}
        onConfirm={prepareDoubleConfirmation}
        confirmText="Ja"
        questionText="Är du säker på att du vill redigera denna dagsaktuella inställning?"
        denyText="Avbryt"
      />
      <OpeningHoursConflictConfirmation
        showPopup={showDoubleConfirmationPopup}
        onClose={() => setShowDoubleConfirmationPopup(false)}
        onConfirm={() => {
          handleUpdateSpecialHours();
          setShowDoubleConfirmationPopup(false);
        }}
        conflicts={conflicts}
        courts={courts}
        checkFailed={conflictCheckFailed}
      />
    </Popup>
  );
}

export default EditSpecialHoursPopup;
